import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Loader2, Search, Wine, Camera, Sparkles, Image as ImageIcon, AlertCircle, Upload,
  Archive,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useImageGeneration } from "../../context/ImageGenerationContext";
import DishesGrid from "./DishesGrid";

/**
 * Tab that shows ALL beverages of a restaurant: commercial (from universal catalog)
 * + user-imported (CSV or manual). Provides a button to bulk-assign stock images
 * to commercial beverages and a modal to decide what to do with non-commercial ones.
 */
export function BeveragesTab({ restaurantId, onViewRecipe, reloadKey = 0,
                              onOpenArchived, archivedCount = 0, onArchive }) {
  const { imgGenActive, handleGenerateDishImages } = useImageGeneration();

  const [beverages, setBeverages] = useState([]);
  const [categoriesMap, setCategoriesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyWithoutImage, setOnlyWithoutImage] = useState(false);
  const [archivingId, setArchivingId] = useState(null);

  // Stock-assignment flow
  const [assigning, setAssigning] = useState(false);
  const [nonCommercialDialog, setNonCommercialDialog] = useState(null);
  // { items: [{dish_id, name, category_id, subcategory}], stock_assigned: int }

  const fetchBeverages = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const menus = await api.getMenus(restaurantId);
      const allDishes = [];
      const catMap = {};
      for (const menu of menus) {
        try {
          const [dishes, cats] = await Promise.all([
            api.getDishes(menu.menu_id),
            api.getCategories(menu.menu_id),
          ]);
          cats.forEach(c => { catMap[c.category_id] = c; });
          allDishes.push(...dishes);
        } catch { /* skip */ }
      }
      // Filter only beverages: source universal_beverage, tag bebida, or category
      // that looks like a drink section (Bebidas, Cócteles, Refrescos, Vinos…)
      const BEV_CAT_KEYWORDS = ["bebida", "coctel", "cóctel", "refresco", "vino", "cerveza", "café", "cafe", "té ", "licor", "carta de vinos", "bodega"];
      const bevCatIds = new Set(
        Object.values(catMap)
          .filter(c => {
            const n = (c.name || "").toLowerCase();
            return BEV_CAT_KEYWORDS.some(k => n.includes(k));
          })
          .map(c => c.category_id)
      );
      const filtered = allDishes.filter(d =>
        d.source === "universal_beverage" ||
        (d.tags || []).includes("bebida") ||
        bevCatIds.has(d.category_id) ||
        d.is_commercial_beverage === true
      );
      setCategoriesMap(catMap);
      setBeverages(filtered);
    } catch {
      toast.error("Error al cargar las bebidas");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchBeverages();
  }, [fetchBeverages, reloadKey]);

  const filtered = useMemo(() => {
    let result = beverages;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(b =>
        (b.name || "").toLowerCase().includes(q) ||
        (b.description || "").toLowerCase().includes(q)
      );
    }
    if (onlyWithoutImage) {
      result = result.filter(b => !b.image_url);
    }
    result = [...result].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"));
    return result;
  }, [beverages, search, onlyWithoutImage]);

  const withoutImageCount = useMemo(
    () => beverages.filter(b => !b.image_url).length,
    [beverages]
  );

  const getCategoryName = useCallback((catId) => {
    return categoriesMap[catId]?.name || "Bebidas";
  }, [categoriesMap]);

  // ── Stock assignment ───────────────────────────────────────────────────
  const handleAssignStock = async () => {
    const targets = beverages.filter(b => !b.image_url);
    if (targets.length === 0) {
      toast.info("Todas las bebidas ya tienen imagen");
      return;
    }
    setAssigning(true);
    try {
      const result = await api.assignStockBeverages(
        restaurantId,
        targets.map(b => b.dish_id)
      );
      const stockCount = result.stock_assigned || 0;
      const nonComm = result.non_commercial || [];

      if (stockCount > 0) {
        toast.success(
          `${stockCount} bebida${stockCount > 1 ? "s" : ""} comercial${stockCount > 1 ? "es" : ""} actualizada${stockCount > 1 ? "s" : ""} con imágenes de stock.`,
          { duration: 4000 }
        );
      }

      // Refresh list to reflect newly assigned stock images
      await fetchBeverages();

      // Open modal ONLY if there are non-commercial beverages to decide
      if (nonComm.length > 0) {
        setNonCommercialDialog({ items: nonComm, stock_assigned: stockCount });
      } else if (stockCount === 0) {
        toast.info("No se detectaron bebidas comerciales pendientes de imagen.");
      }
    } catch (err) {
      toast.error(err.message || "Error al asignar imágenes de stock");
    } finally {
      setAssigning(false);
    }
  };

  const handleGenerateNonCommercialWithAI = async () => {
    if (!nonCommercialDialog) return;
    const dishIds = nonCommercialDialog.items.map(i => i.dish_id);
    setNonCommercialDialog(null);
    // Use the shared global context so the Navbar badge + polling pick it up
    // (currentRestaurant is inferred from restaurantId via a minimal shape)
    await handleGenerateDishImages({ restaurant_id: restaurantId, cuisine_type: "" }, [], dishIds);
    // refresh after enqueue so the user sees progress
    setTimeout(fetchBeverages, 1500);
  };

  const handleSkipNonCommercial = () => {
    setNonCommercialDialog(null);
    toast.info("Puedes subir las imágenes de estas bebidas más tarde desde cada plato.");
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="beverages-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="beverages-tab">
      {/* Header card with bulk stock-assignment action */}
      <Card className="border-none shadow-soft bg-gradient-to-br from-cyan-50/70 to-cyan-100/30 dark:from-cyan-950/20 dark:to-cyan-900/10">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg shrink-0">
              <Wine className="w-5 h-5 text-cyan-700 dark:text-cyan-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm">Gestiona las imágenes de tus bebidas</p>
                {withoutImageCount > 0 && (
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                    data-testid="beverages-missing-image-badge"
                  >
                    {withoutImageCount} sin imagen
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Las bebidas comerciales (cervezas, vinos, refrescos…) recibirán imágenes de stock sin coste. Para las bebidas de autor podrás elegir entre IA o subirlas manualmente.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={handleAssignStock}
              disabled={assigning || imgGenActive || withoutImageCount === 0}
              variant="outline"
              size="sm"
              className="rounded-full gap-1.5 border-cyan-400/60 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 text-xs"
              data-testid="assign-stock-beverages-btn"
            >
              {assigning
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Analizando...</>
                : <><Camera className="w-3.5 h-3.5" />Actualizar imágenes de bebidas</>
              }
            </Button>
            {imgGenActive && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Generación de imágenes IA en curso…
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search + "only without image" filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar bebida..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="beverages-search-input"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
              <Checkbox
                checked={onlyWithoutImage}
                onCheckedChange={setOnlyWithoutImage}
                data-testid="beverages-filter-no-image"
              />
              <span>Solo sin imagen</span>
            </label>
          </div>
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              {filtered.length === beverages.length ? (
                <span>{beverages.length} bebida{beverages.length !== 1 ? "s" : ""} en total</span>
              ) : (
                <span>Mostrando {filtered.length} de {beverages.length} bebidas</span>
              )}
            </div>
            {archivedCount > 0 && onOpenArchived && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                onClick={onOpenArchived}
                data-testid="open-archived-beverages-btn"
              >
                <Archive className="w-3.5 h-3.5" />
                Ver archivadas ({archivedCount})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Grid */}
      <DishesGrid
        dishes={beverages}
        filteredDishes={filtered.map((b) => ({ ...b, _isBeverage: true }))}
        searchQuery={search}
        onViewRecipe={onViewRecipe}
        onArchive={async (dish) => {
          setArchivingId(dish.dish_id);
          try {
            await api.archiveDish(dish.dish_id);
            // Optimistic UI: remove from grid immediately.
            setBeverages((prev) => prev.filter((d) => d.dish_id !== dish.dish_id));
            onArchive?.();
            // Gmail-style toast with Undo (consistent across the app).
            toast.success(`"${dish.name}" archivada`, {
              description: "Se moverá a Archivadas. Puedes restaurarla en cualquier momento.",
              duration: 5000,
              action: {
                label: "Deshacer",
                onClick: async () => {
                  try {
                    await api.restoreDish(dish.dish_id);
                    toast.success("Restaurada");
                    onArchive?.();
                  } catch (e) {
                    toast.error(e?.message || "No se pudo deshacer");
                  }
                },
              },
            });
          } catch (err) {
            toast.error(err.message || "No se pudo archivar");
          } finally {
            setArchivingId(null);
          }
        }}
        onClearFilters={() => { setSearch(""); setOnlyWithoutImage(false); }}
        busyId={archivingId}
        emptyTitle="Sin bebidas aún"
        emptyDescription="Añade bebidas desde tus menús o impórtalas desde un CSV para verlas aquí."
      />

      {/* Non-commercial decision dialog */}
      <Dialog
        open={!!nonCommercialDialog}
        onOpenChange={(o) => { if (!o) setNonCommercialDialog(null); }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="non-commercial-beverages-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Bebidas no comerciales detectadas
            </DialogTitle>
            <DialogDescription>
              Hemos detectado {nonCommercialDialog?.items?.length || 0} bebida{(nonCommercialDialog?.items?.length || 0) !== 1 ? "s" : ""} no comercial{(nonCommercialDialog?.items?.length || 0) !== 1 ? "es" : ""} (cócteles, bebidas de autor, etc.) que no se pueden asociar a una imagen de stock. ¿Cómo quieres generar sus imágenes?
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[240px] overflow-y-auto border rounded-lg bg-muted/40 p-2 space-y-1">
            {(nonCommercialDialog?.items || []).map(item => (
              <div
                key={item.dish_id}
                className="flex items-center gap-2 px-2 py-1.5 text-sm"
                data-testid={`non-commercial-item-${item.dish_id}`}
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="flex-1 truncate">{item.name}</span>
                {item.subcategory && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                    {item.subcategory}
                  </Badge>
                )}
              </div>
            ))}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleSkipNonCommercial}
              data-testid="skip-non-commercial-btn"
              className="gap-1.5"
            >
              <Upload className="w-4 h-4" />
              Las cargaré yo más tarde
            </Button>
            <Button
              onClick={handleGenerateNonCommercialWithAI}
              data-testid="generate-non-commercial-ai-btn"
              className="gap-1.5"
            >
              <Sparkles className="w-4 h-4" />
              Generar con IA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BeveragesTab;
